import type { ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import { queryClient } from './queryClient';
import { Toaster } from '@ui/Toaster';
import { AppErrorBoundary } from '@core/errors/AppErrorBoundary';
import { DialogHost } from '@ui/dialog/DialogHost';

/**
 * Application-wide providers.
 *
 * `DialogHost` is mounted ONCE here and renders a stack, not a single dialog —
 * nesting matters. `dashboard.component.ts:1202` opens a confirm dialog from inside
 * another confirm's result handler, and `attributes-dialog` opens a dirty-check
 * confirm over itself.
 *
 * ⚠️ `Toaster` COMES FIRST, BEFORE `{children}`, AND THAT ORDER IS LOAD-BEARING — it is not styling.
 * Sonner's `Toaster` seeds `useState([])` and subscribes to the toast store from a mount EFFECT, and
 * `Observer.addToast` publishes only to whoever is subscribed at that moment; a toast raised earlier
 * stays in the store but the component never reads that initial array. React runs sibling effects in
 * tree order, so with `{children}` first, ANY TOAST FIRED FROM A FIRST-MOUNT EFFECT WAS SILENTLY
 * DROPPED — no error, no toast, nothing to debug. Found via SSO: the redirect lands on `/login`, which
 * establishes the session from an effect and toasts the outcome, and neither the success nor the failure
 * message ever appeared.
 *
 * Moving it earlier is safe because position is `fixed` and sonner sets `z-index: 999999999`, an order
 * of magnitude above the `z-[1200]` dialogs — DOM order cannot change what paints on top.
 */
export function Providers({ children }: { children: ReactNode }) {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={300} skipDelayDuration={0}>
          <Toaster />
          {children}
          <DialogHost />
        </TooltipProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
